import React from 'react';
import { cn } from '../../lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// Talent Page Card Variants - Based on Amazon Design System but with zero padding
const talentCardVariants = cva(
  // Base styles with zero padding
  'rounded-lg bg-card text-card-foreground transition-all duration-200 p-0',
  {
    variants: {
      variant: {
        // Default card with subtle border
        default: 'border border-[var(--border-default)] bg-white hover:shadow-md',
        // Elevated card with more shadow
        elevated: 'border border-[var(--border-light)] bg-white shadow-md hover:shadow-lg',
        // Outlined card with Amazon orange accent
        outlined: 'border border-[var(--amazon-orange)] bg-white hover:bg-[var(--amazon-orange)]/5',
        // Filled card with background color
        filled: 'border border-[var(--border-light)] bg-[var(--background-secondary)] hover:bg-[var(--background-secondary)]/80',
        // Interactive card for clickable items
        interactive: 'border border-[var(--border-default)] bg-white hover:border-[var(--amazon-orange)] hover:shadow-md cursor-pointer',
        // Borderless card variant
         borderless: 'bg-white hover:shadow-md',
         // Static card variant (no border, no hover effects, no shadow)
         static: 'bg-white shadow-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const talentCardHeaderVariants = cva(
  'flex flex-col space-y-1.5 p-0',
  {
    variants: {
      size: {
        sm: 'pb-3',
        default: 'pb-4',
        lg: 'pb-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const talentCardTitleVariants = cva(
  'font-[var(--font-weight-semibold)] leading-none tracking-tight',
  {
    variants: {
      size: {
        sm: 'text-[var(--font-size-lg)]',
        default: 'text-[var(--font-size-xl)]',
        lg: 'text-[var(--font-size-2xl)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const talentCardDescriptionVariants = cva(
  'text-[var(--text-secondary)]',
  {
    variants: {
      size: {
        sm: 'text-[var(--font-size-sm)]',
        default: 'text-[var(--font-size-sm)]',
        lg: 'text-[var(--font-size-base)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const talentCardContentVariants = cva(
  'p-0',
  {
    variants: {
      size: {
        sm: 'pt-0',
        default: 'pt-0',
        lg: 'pt-0',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const talentCardFooterVariants = cva(
  'flex items-center p-0',
  {
    variants: {
      size: {
        sm: 'pt-3',
        default: 'pt-4',
        lg: 'pt-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface TalentCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof talentCardVariants> {
  asChild?: boolean;
}

export interface TalentCardHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof talentCardHeaderVariants> {}

export interface TalentCardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof talentCardTitleVariants> {}

export interface TalentCardDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof talentCardDescriptionVariants> {}

export interface TalentCardContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof talentCardContentVariants> {}

export interface TalentCardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof talentCardFooterVariants> {}

const TalentPageCard = React.forwardRef<HTMLDivElement, TalentCardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(talentCardVariants({ variant, className }))}
      {...props}
    />
  )
);
TalentPageCard.displayName = 'TalentPageCard';

const TalentPageCardHeader = React.forwardRef<HTMLDivElement, TalentCardHeaderProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(talentCardHeaderVariants({ size, className }))}
      {...props}
    />
  )
);
TalentPageCardHeader.displayName = 'TalentPageCardHeader';

const TalentPageCardTitle = React.forwardRef<HTMLParagraphElement, TalentCardTitleProps>(
  ({ className, size, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h3
      ref={ref}
      className={cn(talentCardTitleVariants({ size, className }))}
      {...props}
    />
  )
);
TalentPageCardTitle.displayName = 'TalentPageCardTitle';

const TalentPageCardDescription = React.forwardRef<HTMLParagraphElement, TalentCardDescriptionProps>(
  ({ className, size, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(talentCardDescriptionVariants({ size, className }))}
      {...props}
    />
  )
);
TalentPageCardDescription.displayName = 'TalentPageCardDescription';

const TalentPageCardContent = React.forwardRef<HTMLDivElement, TalentCardContentProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(talentCardContentVariants({ size, className }))}
      {...props}
    />
  )
);
TalentPageCardContent.displayName = 'TalentPageCardContent';

const TalentPageCardFooter = React.forwardRef<HTMLDivElement, TalentCardFooterProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(talentCardFooterVariants({ size, className }))}
      {...props}
    />
  )
);
TalentPageCardFooter.displayName = 'TalentPageCardFooter';

export {
  TalentPageCard,
  TalentPageCardHeader,
  TalentPageCardTitle,
  TalentPageCardDescription,
  TalentPageCardContent,
  TalentPageCardFooter,
  talentCardVariants,
};

// Usage examples:
// <TalentPageCard variant="elevated">
//   <TalentPageCardHeader>
//     <TalentPageCardTitle>Card Title</TalentPageCardTitle>
//     <TalentPageCardDescription>Card description text</TalentPageCardDescription>
//   </TalentPageCardHeader>
//   <TalentPageCardContent>
//     <p>Card content goes here</p>
//   </TalentPageCardContent>
//   <TalentPageCardFooter>
//     <button>Action</button>
//   </TalentPageCardFooter>
// </TalentPageCard>